"use client";

import { RICH_TEXT_CONTENT_CLASS } from "@gettactic/components/src/form/TextEditorInput/rich-text-content";
import { cn } from "@gettactic/helpers";
import {
  isRichTextEmpty,
  RICH_TEXT_ALLOWED_TAGS,
  toRichTextHtml
} from "@gettactic/helpers/src/workplace-requests/richText";
import React from "react";
import sanitizeHtml from "sanitize-html";

interface RichTextBlockProps {
  html: string | null | undefined;
  className?: string;
}

/**
 * Renders admin-authored rich text — a request type's instructions, or an info
 * block's body — through the same allow-list the editor is restricted to.
 *
 * The backend sanitizes on write, but this sanitizes again on read: the content
 * is authored by one admin and shown to every employee, and a body stored before
 * the rules tightened would otherwise render whatever it happens to contain.
 *
 * Deliberately not {@link TextEditorContent}: that renderer is shared by News,
 * Questionnaires and reservation cards, and allows far more than these bodies do.
 *
 * Returns nothing at all for an empty body — a blank framed card reads as a
 * loading failure, so callers that draw a frame should skip it too.
 */
export const RichTextBlock: React.FC<RichTextBlockProps> = ({
  html,
  className
}) => {
  if (isRichTextEmpty(html)) {
    return null;
  }

  const clean = sanitizeHtml(toRichTextHtml(html), {
    allowedTags: [...RICH_TEXT_ALLOWED_TAGS],
    allowedAttributes: { a: ["href", "target", "rel"] },
    allowedSchemes: ["http", "https", "mailto", "tel"],
    // Instructions point somewhere else by definition, so a link must never
    // navigate the form away mid-request, and `noopener` keeps the destination
    // from reaching back into this tab.
    transformTags: {
      a: (tagName, attribs) => ({
        tagName,
        attribs: { ...attribs, target: "_blank", rel: "noopener noreferrer" }
      })
    }
  });

  return (
    <div
      // The same styling the editor renders with, so what the admin wrote is
      // what the employee reads — bullets, numbers and underlined links included.
      className={cn(
        "max-w-none text-neutral-700 text-sm",
        RICH_TEXT_CONTENT_CLASS,
        className
      )}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized above against a fixed allow-list.
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};
